import type { ReviewQuestion } from '@/types/review/ReviewQuestion';
import type { LocalizedText } from '@/utils/localization';

import { api } from '@/api/api';

export interface ReviewQuestionTranslationsUpdateRequest {
    label: LocalizedText;
    reviewQuestionOptions: { id: string; label: LocalizedText }[];
}

const stripEmpty = (dict: Record<string, string>): Record<string, string> =>
    Object.fromEntries(Object.entries(dict).filter(([, v]) => v.trim() !== ''));

const transformToBackendFormat = (question: ReviewQuestion) => {
    return {
        id: question.id,
        questionType: question.questionType,
        label: stripEmpty(question.label || {}),
        usefulTip: stripEmpty(question.usefulTip || {}),
        sortOrder: question.sortOrder || 0,
        isAnswerMandatory: question.isAnswerMandatory ?? false,
        isCommentAllowed: question.isCommentAllowed ?? true,
        isIncludedInBestOrWorstQuestions: question.isIncludedInBestOrWorstQuestions ?? true,
        isIncludedInScoreCalculation: question.isIncludedInScoreCalculation ?? true,
        status: question.status || 'Active',
        maximumChoices: question.maximumChoices,
        hasOtherOption: question.hasOtherOption || false,
        reviewQuestionOptions: question.reviewQuestionOptions || [],
        companyValueId: question.companyValueId,
    };
};

export default {
    async getQuestionsByCompanyValueId(companyValueId: string): Promise<ReviewQuestion[]> {
        const response = await api.get('/CompanyValueQuestions', {
            params: { companyValueId },
        });
        return response.data;
    },

    async createQuestion(request: ReviewQuestion): Promise<ReviewQuestion> {
        const response = await api.post(
            '/CompanyValueQuestions',
            transformToBackendFormat(request),
        );
        return response.data;
    },

    async updateQuestion(id: string, request: ReviewQuestion): Promise<ReviewQuestion> {
        const response = await api.put(
            `/CompanyValueQuestions/${id}`,
            transformToBackendFormat(request),
        );
        return response.data;
    },

    async updateQuestionTranslations(
        id: string,
        request: ReviewQuestionTranslationsUpdateRequest,
    ): Promise<ReviewQuestion> {
        const response = await api.patch(`/CompanyValueQuestions/${id}/translations`, request);
        return response.data;
    },

    async reorderQuestions(
        companyValueId: string,
        orderedQuestionIds: string[],
    ): Promise<ReviewQuestion[]> {
        const response = await api.patch('/CompanyValueQuestions/reorder', {
            companyValueId,
            questionIds: orderedQuestionIds,
        });
        return response.data;
    },

    async deleteQuestion(id: string): Promise<ReviewQuestion> {
        const response = await api.delete(`/CompanyValueQuestions/${id}`);
        return response.data;
    },

    async toggleActive(id: string): Promise<ReviewQuestion> {
        const response = await api.patch(`/CompanyValueQuestions/${id}/toggle-active`);
        return response.data;
    },
};
